import { usePrint } from '@abyss/web/hooks/usePrint';
import { globalCss } from '@abyss/web/tools/styled';

import { ConstantsLagoon } from '../../common/ConstantsLagoon';
import { useFeatureFlag } from '../useFeatureFlag';

// Injected into every print context to ensure CJK (Chinese/Japanese/Korean)
// characters are visible. The active web fonts (AbyssOptumSans / AbyssUHCSans)
// have no CJK glyphs, so without this fallback Chinese translations render as
// blank boxes in the printed PDF.
export const CJK_PRINT_FONT_CSS = `
  @font-face {
    font-family: 'PSXCJKPrint';
    src: local('PingFang TC'), local('Hiragino Sans GB'),
         local('Microsoft JhengHei'), local('Noto Sans CJK TC'),
         local('WenQuanYi Micro Hei'), local('Arial Unicode MS');
    unicode-range: U+2E80-2EFF, U+2F00-2FDF, U+3040-9FFF,
                   U+F900-FAFF, U+FE30-FE4F;
  }
  @media print {
    html, body, * {
      font-family: 'AbyssOptumSans', 'AbyssUHCSans', 'PSXCJKPrint',
                   Arial, sans-serif !important;
    }
  }
`;

const injectCJKFontIntoDocument = (doc: Document) => {
  if (!doc.getElementById('psx-cjk-print-font')) {
    const style = doc.createElement('style');
    style.id = 'psx-cjk-print-font';
    style.textContent = CJK_PRINT_FONT_CSS;
    doc.head.appendChild(style);
  }
};

const printGlobalStyles = globalCss({
  '@media print': {
    '#global-header': {
      display: 'none',
    },
    '#global-main': {
      display: 'none',
    },
    '#global-footer': {
      display: 'none',
    },
    '[class*="abyss-toast"]': {
      display: 'none !important',
    },
  },
});

export const usePrintPSX = () => {
  const [enablePSXPrintSolution] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_PSX_PRINT,
  ]);
  const print = usePrint();
  const printPSXPage = () => {
    // Always inject CJK font support so Chinese/Japanese/Korean translations
    // render correctly in every print path (current window or new window).
    injectCJKFontIntoDocument(document);

    if (enablePSXPrintSolution) {
      printGlobalStyles();
      const isSafari = /^((?!chrome|android).)*safari/i.test(
        navigator.userAgent
      );

      // Intercept window.open so we can inject CJK CSS into whatever new
      // window Abyss opens for printing (Chrome/Firefox and Safari).
      const originalOpen = window.open.bind(window);
      (window as any).open = function (
        ...args: Parameters<typeof window.open>
      ) {
        const newWin = originalOpen(...args);
        (window as any).open = originalOpen; // restore immediately
        if (newWin) {
          try {
            if (isSafari) {
              newWin.document.write(
                `<!DOCTYPE html><html><head><meta name="viewport" content="width=${
                  window.innerWidth || 1200
                },initial-scale=1"><style>${CJK_PRINT_FONT_CSS}</style></head><body></body></html>`
              );
              newWin.document.close();
            } else {
              injectCJKFontIntoDocument(newWin.document);
            }
          } catch {
            /* cross-origin guard */
          }
        }
        return newWin;
      };

      if (isSafari) {
        print.printPage(() => {}, { openNewWindow: true });
      } else {
        print.printPage();
      }
    } else {
      print.printPage(() => {}, {
        openNewWindow: false,
      });
    }
  };
  return { printPSXPage };
};
